<!--
// app/pages/[region]/[[news]].vue
-->
<script setup lang="ts">
import type { Post } from "~/src/post/domain/models/Post";
import type { NitroFetchOptions } from "nitropack";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import postUiService from "~/src/post/infrastructure/ui-services/postUiService";
import GridNewsHero from "~/app/components/Shared/GridNewsHero.vue";
import BaseNuxtLink from "~/app/components/base/baseNuxtLink.vue";
import { $FEATURED_ITEMS, $PER_PAGE_ITEMS } from "~/src/post/domain/service/PostService";
import CardNewsArticle from "~/app/components/Shared/news/CardNewsArticle.vue";

type Props = {
  items?: number;
  categories?: number[];
  exclude?: number[];
};

//check validationNewsGuard middleware to add news param path for news page in new languages

definePageMeta({
  middleware: ["validation-news-guard", "not-found-king-of-kings-nations"],
});

const setI18nParams = useSetI18nParams();
const { getPosts, localeHasNews } = postUiService();

const localesWithNews = ref([]);

const checkAvailableNewsLocales = async () => {
  const locales = ["en", "es", "de", "fr", "it", "pt"];
  const results = await Promise.all(
    locales.map(async lang => ({
      lang,
      hasNews: await localeHasNews(lang),
    }))
  );
  localesWithNews.value = results.filter(({ hasNews }) => hasNews).map(({ lang }) => lang);
};
await checkAvailableNewsLocales();

setI18nParams({
  en: { news: localesWithNews.value?.includes("en") ? "news" : false },
  es: { news: localesWithNews.value?.includes("es") ? "actualidad" : false },
  de: { news: localesWithNews.value?.includes("de") ? "aktuelles" : false },
  fr: { news: localesWithNews.value?.includes("fr") ? "actualites" : false },
  it: { news: localesWithNews.value?.includes("it") ? "notizie" : false },
  pt: { news: localesWithNews.value?.includes("pt") ? "noticias" : false },
});

const props = withDefaults(defineProps<Props>(), {
  items: $PER_PAGE_ITEMS,
});

const { contextSiteTitle } = useWebContext();
const { region } = useCompetitionStore();
const localePath = useLocalePath();
const route = useRoute();
const { locale, t } = useI18n();
const posts = ref<Post[] | undefined>([]);
const pageNum = ref<number>(1);
const hideLoadMore = ref<boolean>(false);
const loadingMore = ref<boolean>(false);

if (!localesWithNews.value?.includes(locale.value)) {
  throw createError({ statusCode: 404 });
}

const initialOpts: NitroFetchOptions<any> = {
  params: {
    per_page: $PER_PAGE_ITEMS + $FEATURED_ITEMS,
    page: pageNum.value,
    lang: locale.value,
  },
};

if (props.categories && initialOpts.params) {
  initialOpts.params.categories =
    props.categories.length > 1 ? props.categories.join(",") : props.categories[0].toString();
}

if (props.exclude && initialOpts.params) {
  initialOpts.params.exclude =
    props.exclude.length > 1 ? props.exclude.join(",") : props.exclude[0].toString();
}

const initialPosts = await getPosts(initialOpts);

posts.value = initialPosts?.slice($FEATURED_ITEMS, $PER_PAGE_ITEMS);
pageNum.value++;

const loadMore = async () => {
  if (loadingMore.value) return;

  loadingMore.value = true;
  try {
    const opts: NitroFetchOptions<any> = {
      params: {
        per_page: props.items,
        page: pageNum.value,
        lang: locale.value,
      },
    };
    if (props.categories && initialOpts.params) initialOpts.params.categories = props.categories.join(",");
    if (props.exclude && initialOpts.params) initialOpts.params.exclude = props.exclude.join(",");

    const newPosts = await getPosts(opts);

    if (newPosts?.length) {
      pageNum.value++;
      posts.value = [...posts.value, ...newPosts];
    } else {
      hideLoadMore.value = true;
    }
  } finally {
    loadingMore.value = false;
  }
};

useSeoMeta({
  title: t("footer.nav.news"),
  ogTitle: t("footer.nav.news"),
  description: t("meta.description.news.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.news.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <GridNewsHero is-news-page />

  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 mt-12 mx-auto max-w-[1440px]">
    <card-news-article v-for="post in posts" :post="<Post>post"></card-news-article>
  </div>

  <div class="py-6 flex justify-center items-center">
    <base-nuxt-link text-size="text-xs" @click="loadMore" primary v-if="!hideLoadMore" :loading="loadingMore"
      >{{ $t("general.loadMore") }}
    </base-nuxt-link>
  </div>
</template>

<style scoped></style>
